import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { MapView } from '../../components/common/MapView';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Eye, Lock, Users, Wifi, WifiOff } from 'lucide-react';
import { useGpsTracker } from '../../lib/use-gps';

export default function StartPersonalRun() {
  const nav = useNavigate();
  const [vis, setVis] = useState('private');
  // Warm up the GPS while the user is choosing visibility so the live page has
  // an immediate fix the moment they tap "Start Run". `active: false` means we
  // listen for the fix but don't record a path.
  const gps = useGpsTracker({ active: false });
  const acquiring = gps.supported && !gps.error && gps.accuracy == null;

  let gpsLabel = 'Off';
  let gpsClass = 'text-destructive';
  let GpsIcon = WifiOff;
  if (gps.supported && !gps.error) {
    GpsIcon = Wifi;
    if (acquiring) {
      gpsLabel = 'Acquiring…';
      gpsClass = 'text-muted-foreground';
    } else if (gps.accuracy < 15) {
      gpsLabel = `Strong · ±${Math.round(gps.accuracy)}m`;
      gpsClass = 'text-success';
    } else if (gps.accuracy < 40) {
      gpsLabel = `Good · ±${Math.round(gps.accuracy)}m`;
      gpsClass = 'text-primary';
    } else {
      gpsLabel = `Weak · ±${Math.round(gps.accuracy)}m`;
      gpsClass = 'text-destructive';
    }
  } else if (gps.error) {
    gpsLabel = 'Permission denied';
  }

  return (
    <div className="px-4 pt-4 pb-6 space-y-5 min-h-full flex flex-col" data-testid="personal-run-start">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="run-start-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div>
        <p className="rivl-label">Personal Run</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Ready to run?</h1>
      </div>

      <MapView height={220} showUserLocation path={[]} />

      <div className="rivl-card p-4 space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2 text-muted-foreground">
            <GpsIcon className={`h-4 w-4 ${gpsClass}`} /> GPS
          </span>
          <span className={`font-mono font-medium ${gpsClass}`} data-testid="run-start-gps">{gpsLabel}</span>
        </div>
        {gps.error && (
          <p className="text-[11px] text-destructive" data-testid="run-start-gps-error">
            {gps.error}. Allow location access in your browser settings.
          </p>
        )}
        <div className="text-sm">
          <div className="rivl-label mb-2">Visibility</div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { k: 'private', label: 'Private', icon: Lock },
              { k: 'friends', label: 'Friends', icon: Users },
              { k: 'public', label: 'Public', icon: Eye },
            ].map((o) => (
              <button
                key={o.k}
                onClick={() => setVis(o.k)}
                data-testid={`vis-${o.k}`}
                className={`flex flex-col items-center gap-1 p-3 rounded-md border transition-colors ${
                  vis === o.k ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:bg-secondary'
                }`}
              >
                <o.icon className="h-4 w-4" />
                <span className="text-xs font-medium">{o.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <Button asChild className="w-full h-14 rounded-md text-base font-bold mt-auto" data-testid="run-start-btn">
        <Link
          to="/run/live?fresh=1"
          onClick={() => {
            // Persist chosen visibility so RunSummary can pick it up.
            try { sessionStorage.setItem('rivl:run:visibility', vis); } catch { /* private mode */ }
          }}
        >Start Run</Link>
      </Button>
    </div>
  );
}
